import { keyBy } from 'es-toolkit';

import {
  Currency,
  Instrument,
  InstrumentApi,
  InstrumentProductTypeEnum,
  PositionBracket,
  PositionBracketRaw,
  SpecSnapshot,
} from '@flipster/apis/v2/snapshot';
import { BonusCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import Num, { countDecimalPlaces } from '@flipster/utils/num';
import { valueof } from '@flipster/utils/typescript';

export type CurrencyV2InstanceType = ReturnType<typeof currencyV2Instance>;
export type InstrumentV2InstanceType = ReturnType<typeof instrumentV2Instance>;
export type InstrumentV2ForApiInstanceType = ReturnType<typeof instrumentV2ForApiInstance>;

/** VARA 스팟 전용일 때 UI 표시용으로 사용 (VARA_ prefix 제거). URL/API에는 원본 symbol 사용 */
export type InstrumentV2WithDisplaySymbol = InstrumentV2ForApiInstanceType & {
  displaySymbol?: string;
};

export type SnapshotSpec = ReturnType<typeof snapshotSpecResolver>;

const VARA_PREFIX = 'VARA_';

/** VARA_ prefix 제거 (isSpotOnly일 때 유저 표시용) */
export const stripVaraPrefix = (s: string): string => (s.startsWith(VARA_PREFIX) ? s.slice(VARA_PREFIX.length) : s);

/** 표시용 심볼/이름 반환 (display* 필드가 있으면 사용, 없으면 원본) */
export const getDisplaySymbol = (instrument: InstrumentV2WithDisplaySymbol): string =>
  instrument.displaySymbol ?? instrument.symbol;

const currencyV2Instance = (c: Currency) => {
  const isUSDT = c.currency === UsdtCurrency;
  const isBonus = c.currency === BonusCurrency;

  return {
    ...c,
    isUSDT,
    isBonus,
    englishName: isBonus ? 'USDT Bonus' : c.englishName,
    conversionUnit: c.conversionUnit ?? '0.0001',
    getMinConfirmation(networkName: string) {
      const blockchainInfo = c.blockchainInfo.find((bi) => bi.networkName === networkName) ?? c.blockchainInfo[0];
      return blockchainInfo.minConfirmations;
    },
  };
};

const instrumentV2Instance = <I extends Instrument>(i: I) => {
  return {
    ...i,
    currency: i.positionUnit,
    isSpot: i.productType === InstrumentProductTypeEnum.Spot,
    isFutures: i.productType === InstrumentProductTypeEnum.Pswap,
  };
};

const instrumentV2ForApiInstance = (i: InstrumentApi) => {
  // Cross / bracket Tier‑1 ceiling (overridden later by snapshotSpecBunja from position bracket).
  // Optional maxInitLeverageIsolatedMargin passes through via spread — do not fold it into this value.
  const maxLeverageHardLimit = Math.round(Num(1).dividedBy(i.initMarginRate).toNumber());
  // unitOrderQty = lot size
  const tradeQtyPrecision = countDecimalPlaces(i.unitOrderQty);

  return {
    ...instrumentV2Instance(i),
    maxLeverageHardLimit,
    tradeQtyPrecision,
  };
};

export const checkListableInstrument = (i: InstrumentApi, productType: valueof<typeof InstrumentProductTypeEnum>) =>
  i.isListed && i.productType === productType;

export const snapshotSpecResolver = (data: SpecSnapshot) => {
  const instrumentsInstance: InstrumentV2ForApiInstanceType[] = Object.values(data.t.instruments.s).map(
    instrumentV2ForApiInstance,
  );
  // array list 의 경우는 딱 listing 가능한 것들만 내려주기
  const pswapInstruments = instrumentsInstance.filter((i) =>
    checkListableInstrument(i, InstrumentProductTypeEnum.Pswap),
  );
  const spotInstruments = instrumentsInstance.filter((i) => checkListableInstrument(i, InstrumentProductTypeEnum.Spot));
  const instrumentsKeyBySymbol: Record<string, InstrumentV2ForApiInstanceType> = keyBy(
    instrumentsInstance,
    (v) => v.symbol,
  );
  const currencies: CurrencyV2InstanceType[] = Object.values(data.t.currencies.s).map(currencyV2Instance);
  const currenciesKeyByCurrency: Record<string, CurrencyV2InstanceType> = keyBy(currencies, (v) => v.currency);

  const rawPositionBrackets: Record<string, PositionBracketRaw> = data.t.positionBrackets.s;

  const positionBrackets: Record<string, PositionBracket> = Object.fromEntries(
    Object.entries(rawPositionBrackets).map(([key, bracket]) => {
      const newPositionBracket: PositionBracket = {
        ...bracket,
        entries: bracket.entries.map((e, index) => {
          const prev = index > 0 ? bracket.entries[index - 1] : undefined;
          const next = index < bracket.entries.length - 1 ? bracket.entries[index + 1] : undefined;
          return {
            ...e,
            notionalPositionSizeLowerLimit: prev?.notionalPositionSizeUpperLimit || '0',
            minInitLeverage: next?.maxInitLeverage || 0,
          };
        }),
      };
      return [key, newPositionBracket];
    }),
  );

  return {
    pswapInstruments,
    spotInstruments,
    instrumentsKeyBySymbol,
    currencies,
    currenciesKeyByCurrency,
    positionBrackets,
  };
};
